import {
  ShapeConfig,
  Translate,
  AlignParameters,
  Scale,
  _Scale,
} from '../../../../elements/resource/types/shape.type';
import { Container, Graphics, Sprite, Texture } from 'pixi.js';
import {
  StudioCell,
  StudioVideoShapeDescriptor,
  selectMedia,
  StudioAnimation,
} from '../../../store/studio';
import { DragResponse } from '../../../../elements/util/point-controller/point-controller';
import { RectangleController } from '../../../../elements/util/rectangle-controller/rectangle-controller';
import { StudioSceneShape } from '../general/root/studio-scene-shape';
import { StudioShape } from '../../../store/studio/shapes/studio-shape';
import gsap from 'gsap';
import { RectangleElement } from '../primitive/rectangle-element';
import { Subscription } from 'rxjs';

export type VideoAnimationId =
  | 'fade'
  | 'float-left'
  | 'float-right'
  | 'float-top'
  | 'float-bottom'
  | 'zoom-in'
  | 'zoom-out'
  | 'zoom-in-out';

export interface VideoAnimationDescriptor {
  id?: VideoAnimationId;
  baseType: 'appear' | 'disappear';
  duration?: number;
  float?: 'top' | 'bottom' | 'right' | 'left';
  scale?: {
    from?: number;
    to?: number;
  };
  next?: VideoAnimationDescriptor;
}

export interface VideoShapeConfig extends ShapeConfig {
  cellKey?: string;
}

type Clip = { from: number; to: number };

export class VideoShape extends StudioShape<StudioVideoShapeDescriptor> {
  private videoElement: HTMLVideoElement;
  private videoTexture: Texture;
  private sprite: Sprite;
  private playIcon: Graphics;

  container: Container;
  timeline: gsap.core.Timeline;
  private _width: number;
  private _height: number;
  private _scale: { x: number; y: number } = { x: 1, y: 1 };
  private _translate: Translate = { x: 0, y: 0 };
  get s3Id() {
    return this.video.s3Id;
  }

  get videoPlayer() {
    return this.studioService.videoPlayerService;
  }
  get cs() {
    return this.shape.cs;
  }

  set s3Id(val: string) {
    this.video.s3Id = val;
  }

  get cell() {
    return this.descriptor.cell;
  }
  getType(): string {
    return 'video-shape';
  }

  get rWidth() {
    return this._width * this._scale.x;
  }

  get rHeight() {
    return this._height * this._scale.y;
  }

  initialised = false;

  get videoInstance() {
    return this.studioService.videoInstances[this.descriptor.videoInstanceId];
  }

  get videoId() {
    return this.descriptor.videoId;
  }

  get video() {
    return this.studioService.videos[this.videoId];
  }

  get studioService() {
    return this.shape.studioService;
  }

  get paddingValue() {
    return this.descriptor.cell.padding == undefined
      ? 0
      : (this.descriptor.cell.padding as number);
  }

  get from() {
    return this.descriptor.from;
  }

  get to() {
    return this.descriptor.to;
  }
  loadPromise: Promise<void> = Promise.resolve();
  activeSub: Subscription;
  constructor(
    shape: StudioSceneShape,
    id: string,
    data: StudioVideoShapeDescriptor,
    public loaded?: () => void,
  ) {
    super(shape, id, data);
    // -- //
    console.log('video-shape', data);
    this.loadPromise = this.waitForVideo().then(() => {
      this.init();
      loaded?.();
    });

    this.videoPlayer.initializePlayer([this.descriptor]);

    this.activeSub = this.videoPlayer.activeVideo$.subscribe(el => {
      if (this.removed) return;
      if (!el) return;
      // console.log('video-shape > activeVideo$', el);
      this.setActiveVideoElement(el);
    });
  }

  waitForVideo() {
    const cnt = 0;
    return new Promise<void>((resolve, reject) => {
      const interval = setInterval(() => {
        if (this.studioService.videos[this.descriptor.videoId]) {
          clearInterval(interval);
          resolve();
        }

        // -- //
        if (cnt > 10) {
          clearInterval(interval);
          resolve();
        }
      }, 100);
    });
  }

  async init() {
    // console.log('init-video', this.videoInstance);

    const id = this.descriptor.videoId;
    const videoUrl = id?.startsWith('http')
      ? id
      : `https://private.cdn.inwedio.com/videos/${id}/video.${this.video.extension}`;

    let thumbnailUrl = `https://private.cdn.inwedio.com/videos/${id}/thumbnail.jpg`;
    if (this.descriptor.clips?.length) {
      const [clip] = this.descriptor.clips;
      this.descriptor.from = clip.from;
      this.descriptor.to = clip.to;
      this.initRC();
      thumbnailUrl = `https://private.cdn.inwedio.com/videos/${id}/thumbnail-${clip.id}.jpg`;
    }
    // -- //
    this.initThumbnailPreview(thumbnailUrl);
    this.doAlign(this.cell);
  }

  private initThumbnailPreview(thumbnailUrl: string) {
    const texture = Texture.from(thumbnailUrl);
    this.sprite = new Sprite(texture);
    this.container.addChild(this.sprite);
  }

  private initVideoPreview(videoUrl: string) {
    this.videoElement = document.createElement('video');
    this.videoElement.crossOrigin = 'anonymous';
    this.videoElement.muted = true;
    this.videoElement.preload = 'metadata';
    this.videoElement.autoplay = false;
    this.videoElement.loop = false;

    // IMPORTANT: don't set currentTime before src in some browsers
    this.videoElement.onloadedmetadata = () => {
      if (this.removed) return;

      // If first load: create the sprite once. We want correct dimensions.
      if (!this.sprite) {
        this.createVideoSprite();
        this.loaded?.();
      }

      // Now seek to descriptor.from (or leave at 0 if undefined)
      this.scrubToFromIfNeeded();
    };

    this.videoElement.onseeked = () => {
      if (this.removed) return;

      // seeking later should NOT recreate sprite
      this.videoTexture?.update(); // refresh frame in pixi
    };

    this.videoElement.onerror = () => {
      if (this.removed) return;
      // this.createPlaceholder();
    };

    this.videoElement.src = videoUrl;
    this.videoElement.load();
  }

  private scrubToFromIfNeeded() {
    const v = this.videoElement;
    const from = this.descriptor?.from;

    if (!v) return;
    if (from == null) return; // undefined/null => no scrub
    if (!Number.isFinite(from)) return;

    // If metadata isn't ready yet, wait once.
    if (
      v.readyState < 1 /* HAVE_METADATA */ ||
      !Number.isFinite(v.duration) ||
      v.duration <= 0
    ) {
      v.addEventListener('loadedmetadata', () => this.scrubToFromIfNeeded(), {
        once: true,
      });
      return;
    }

    // Clamp from into valid range
    const clamped = Math.max(0, Math.min(from, Math.max(0, v.duration - 0.05)));

    // Avoid redundant seeks (helps if update() calls coverAlign etc.)
    if (Math.abs((v.currentTime ?? 0) - clamped) < 0.02) {
      // still ensure texture reflects current frame
      this.videoTexture?.update();
      return;
    }

    try {
      v.currentTime = clamped;
    } catch {
      // Some browsers can throw if seek not allowed yet; try once video can play
      v.addEventListener(
        'canplay',
        () => {
          try {
            v.currentTime = clamped;
          } catch {}
        },
        { once: true },
      );
    }
  }

  private createVideoSprite() {
    this.videoElement.autoplay = false;
    this.videoElement.loop = false;
    // Create texture from video frame // -- //
    this.videoTexture = Texture.from(this.videoElement);

    this.videoElement.pause();

    this.sprite = new Sprite(this.videoTexture);
    this.container.addChild(this.sprite);

    this._width = this.videoElement.videoWidth || 320;
    this._height = this.videoElement.videoHeight || 180;

    // this.addPlayIcon();
    this.doAlign(this.cell);

    this.container.visible = true;
    this.initialised = true;

    // Initialize rectangle controller after video is loaded
  }

  private createPlaceholder() {
    if (this.removed) {
      return;
    }
    // Create a placeholder rectangle for failed video loads
    const placeholder = new Graphics();
    placeholder.beginFill(0x333333);
    placeholder.drawRect(0, 0, 320, 180);
    placeholder.endFill();
    this.container.addChild(placeholder);

    this._width = 320;
    this._height = 180;

    // this.addPlayIcon();
    this.doAlign(this.cell);

    this.container.visible = true;
    this.initialised = true;
  }

  private addPlayIcon() {
    // Add play button overlay
    this.playIcon = new Graphics();
    this.playIcon.beginFill(0xffffff, 0.8);
    this.playIcon.moveTo(0, 0);
    this.playIcon.lineTo(30, 17);
    this.playIcon.lineTo(0, 34);
    this.playIcon.closePath();
    this.playIcon.endFill();

    // Center the play icon
    this.playIcon.x = (this._width - 30) / 2;
    this.playIcon.y = (this._height - 34) / 2;

    this.container.addChild(this.playIcon);
  }

  reset(): void {
    this.container.scale.x = 1;
    this.container.scale.y = 1;
    this.container.alpha = 1;
    this.alignInCell();
  }

  resetAnimation() {
    if (!this.videoElement) return;

    if (this.maskRectangle?.element) {
      gsap.killTweensOf(this.maskRectangle.element);
    }

    this.sprite.mask = null;
    this.maskRectangle?.remove();
    this.maskRectangle = null;

    this.videoElement.pause();

    const v = this.videoElement;

    if (v.readyState >= 1) {
      v.currentTime = 0;
    } else {
      v.addEventListener('loadedmetadata', () => (v.currentTime = 0), {
        once: true,
      });
    }

    if (this.playIcon) {
      this.playIcon.visible = true;
    }
  }

  doAlign(cell: StudioCell, params: AlignParameters = {}): Translate {
    // super.doAlign(cell, params); // -- //

    if (!cell) {
      return;
    }

    this.coverAlign();
    return;
  }

  update(desc: StudioVideoShapeDescriptor) {
    this.descriptor = desc;
    this.coverAlign();
  }

  coverAlign() {
    // -- // -- //

    const { widthRatio } = this.descriptor.cell;
    const { width, height } = this.video;

    const [cellWidth, cellHeight] = this.grossDims;
    const scale = Math.max(cellWidth / width, cellHeight / height);
    // -- // -- // -- // -- // -- //

    this._scale.x = scale * (widthRatio || 1);
    this._scale.y = scale * (widthRatio || 1);

    this.applyScale(this._scale);

    const [x, y] = this.grossCoords;
    const dx = (cellWidth - width * this._scale.x) / 2;
    const dy = (cellHeight - height * this._scale.y) / 2;

    this._translate.x = dx;
    this._translate.y = dy;

    this.setPosition(x + this._translate.x, y + this._translate.y);
  }

  rc: RectangleController;
  private unmovedDims: [number, number];
  private draggedDims: [number, number];

  initRC() {
    if (!this.initialised) {
      console.warn('VideoShape not initialized yet, cannot create RC');
      return;
    }

    const width = this._width * this._scale.x;
    const height = this._height * this._scale.y;
    this.rc = new RectangleController(
      this,
      {
        id: `video-${this.descriptor.videoInstanceId}`,
        width,
        // offset: [this.container.x, this.container.y],
        height,
        // noStroke: false,
        fill: true,
        noScale: false,
        hiddenMode: true,
        fillOpacity: 0.001,
        skipRC: {
          center: true,
          'center-top': true,
          'center-bottom': true,
          'left-center': true,
          'right-center': true,
        },
        startDrag: () => {
          this.unmovedDims = [width, height];
        },
        drag: resp => {
          const { width: newWidth, height: newHeight } = resp;
          this.draggedDims = [newWidth, newHeight];

          // Update video shape dimensions during drag
          if (this.cell) {
            const scaleX = newWidth / width;
            const scaleY = newHeight / height;

            this._scale.x *= scaleX;
            this._scale.y *= scaleY;
            this.applyScale(this._scale);
          }
        },
        endDrag: () => {
          if (!this.draggedDims || !this.unmovedDims) {
            return;
          }

          const [_width, _height] = this.unmovedDims;
          const [newWidth, newHeight] = this.draggedDims;

          // Update cell dimensions relative to original size
          if (this.cell) {
            this.cell.w = newWidth / _width;
            this.cell.h = newHeight / _height;

            // Save the changes
            this.save();
          }

          // Reset dragged dims
          this.draggedDims = null;
        },
        clicked: () => this.onVideoClicked(),
        mouseover: () => this.onMouseOver(),
        mouseout: () => this.onMouseOut(),
      },
      this.circleContainer,
      this.circleContainer,
    );

    this.rc.rect.patch({
      stroke: '#0191f3',
      // 'stroke-width': 0,
    });

    // this.rc.show();
    // this.rc.hide();
    this.onMouseOut();
  }

  private onVideoClicked() {
    console.log('VideoShape clicked:', this.descriptor.videoInstanceId);

    // Show rectangle controller
    this.rc?.show();

    // Dispatch selection event
    if (this.videoInstance) {
      this.shape.store.dispatch(
        selectMedia({
          videoInstanceId: this.videoInstance.id,
          videoId: this.video.id,
        }),
      );
    }
  }

  private onMouseOver() {
    this.rc?.show();
  }

  private onMouseOut() {
    this.rc?.hide();
  }

  private saveCellToStore() {
    // Dispatch save action to store
    // this.shape.store.dispatch(
    //   saveCell({
    //     cellId: `video-${this.descriptor.videoInstanceId}`,
    //     cell: this.cell,
    //   }),
    // );
  }

  get dims() {
    return [
      this.video.width * this._scale.x,
      this.video.height * this._scale.y,
    ];
  }

  rcDrag(resp: DragResponse) {
    const { dx, dy } = resp;
    this._translate.x += dx;
    this._translate.y += dy;
    this.container.position.set(this._translate.x, this._translate.y);
  }

  endRCDrag() {
    // this._saveTranslate(this._translate);
  }

  applyScale(scale: Scale) {
    this._scale = scale as _Scale;
    this.container.scale = scale as _Scale;
  }

  alignInCell() {
    if (!this.cell) return;

    const { w: _w, h: _h, x, y } = this.cell;

    const translate: Translate = {
      x: x + (_w - this._width * this._scale.x) / 2,
      y: y + (_h - this._height * this._scale.y) / 2,
    };

    this.applyTranslate(translate);

    // Update play icon position after scaling
    if (this.playIcon) {
      this.playIcon.x = (this._width * this._scale.x - 30) / 2;
      this.playIcon.y = (this._height * this._scale.y - 34) / 2;
    }
  }

  applyTranslate(translate: Translate) {
    this._translate = translate;
    this.container.position = translate;
  }

  /**
   * Play the video
   */
  play() {
    if (this.videoElement) {
      // Hide play icon when playing
      if (this.playIcon) {
        this.playIcon.visible = false;
      }
      // console.log('VideoShape play');
      this.videoElement.muted = false;
      this.videoElement.play().catch(err => {
        console.warn('Video play failed:', err);
        // Try muted autoplay as fallback
        this.videoElement.muted = true;
        this.videoElement.play();
      });
    }
  }

  /**
   * Pause the video
   */
  pause() {
    if (this.videoElement) {
      this.videoElement.pause();
      // Show play icon when paused
      if (this.playIcon) {
        this.playIcon.visible = true;
      }
    }
  }

  wasPlayingBeforeMasterPause = false;
  onMasterPause() {
    if (!this.videoElement) return;
    this.wasPlayingBeforeMasterPause = !this.videoElement.paused;
    this.pause();
  }

  onMasterResume() {
    if (!this.videoElement) return;
    if (this.wasPlayingBeforeMasterPause) this.play();
    this.wasPlayingBeforeMasterPause = false;
  }

  /**
   * Stop the video and reset to beginning
   */
  stop() {
    if (this.videoElement) {
      this.videoElement.pause();
      this.videoElement.currentTime = 0;
      // Show play icon when stopped
      if (this.playIcon) {
        this.playIcon.visible = true;
      }
    }
  }

  /**
   * Check if video is playing
   */
  get isPlaying(): boolean {
    return this.videoElement && !this.videoElement.paused;
  }

  remove() {
    const v = this.videoElement;
    if (v) {
      v.onerror = null;
      v.onloadeddata = null;
      v.onseeked = null;

      v.pause();
      v.removeAttribute('src'); // better than src=''
      v.load(); // resets the element
      this.videoElement = null;
    }
    super.remove();
  }

  /**
   * Animate the video shape with gsap
   * Supports appear/disappear animations with optional float direction and scale
   */
  waitForVideoEnd(): Promise<void> {
    return new Promise(resolve => {
      const v = this.videoElement;
      console.log('waitForVideoEnd', v);
      if (!v) return resolve();

      // already ended / not playable
      console.log('waitForVideoEnd', v.ended);
      if (v.ended) return resolve();

      const done = () => {
        v.removeEventListener('ended', done);
        resolve();
      };

      v.addEventListener('ended', done, { once: true });
    });
  }

  activeVideoEl: HTMLVideoElement;
  private setActiveVideoElement(el: HTMLVideoElement) {
    this.activeVideoEl = el;

    // console.log('setActiveVideoElement', el);
    if (!this.sprite) {
      this.videoTexture = Texture.from(el);
      this.sprite = new Sprite(this.videoTexture);
      this.container.addChild(this.sprite);
      console.log('setActiveVideoElement > added sprite');
      // If you do coverAlign / dimensions / play icon — do it here once.
      // this.addPlayIcon(); // -- //
      // this.coverAlign(); //
      return;
    }

    // Swap sprite texture to new element (seamless switch)
    const newTex = Texture.from(el, {
      resourceOptions: {
        autoPlay: false,
      },
    });
    this.sprite.texture = newTex;

    try {
      el.pause();
    } catch {
      // -- //
    }
    // Prevent texture buildup
    try {
      this.videoTexture?.destroy(true);
    } catch {}
    this.videoTexture = newTex;
  }

  /** Build the ordered clip list for THIS shape */
  private getClips(): Clip[] {
    // Option A: you store clips[] on descriptor
    const clips = (this.descriptor as any).clips as Clip[] | undefined;
    if (clips?.length) return clips;

    // Option B: single [from,to]
    const from = (this.descriptor as any).from ?? 0;
    const to = (this.descriptor as any).to ?? from;
    return [{ from, to }];
  }

  /** Compute total play duration for GSAP hold */
  private totalDuration(clips: Clip[]) {
    let sum = 0;
    for (const c of clips) sum += Math.max(0, (c.to ?? 0) - (c.from ?? 0));
    return sum;
  }

  /**
   * Plays the VideoShape clips via the injected player service.
   * Assumes: ONE VideoShape is "active" for playback at a time in your project.
   */
  gsapAnimateStep(animation: StudioAnimation): gsap.core.Timeline {
    this.timeline = gsap.timeline();

    // --- Resolve url --- //
    const video = this.studioService.videos[this.videoId];
    if (!video) return this.timeline;

    const url = video.url;
    // --- Resolve clips ---
    const clips = this.getClips();
    console.log('clips', clips);
    console.log('url', url);
    if (!clips.length) return this.timeline;

    // --- Hold GSAP for full playback time (so the step lasts while video plays) ---
    const hold = this.totalDuration(clips);
    this.timeline.to({}, { duration: hold });

    // --- Start playback at time=0 of GSAP step ---
    this.timeline.call(
      () => {
        void (async () => {
          try {
            // 1) Build the input format expected by the simple player service:
            //    one "video" with N clips.

            // 2) Start playing sequentially (A/B swapping inside the service)
            await this.videoPlayer.play();
          } catch (e) {
            console.warn('VideoShape playback failed:', e);
          }
        })();
      },
      undefined,
      0,
    );

    // --- Cleanup at end of step ---
    this.timeline.eventCallback('onComplete', () => {
      this.videoPlayer.stop();
      // if you want “show play icon again”, do it here
      // this.stopVisual();
    });

    return this.timeline;
  }

  _remove() {
    this.removed = true;
    this.activeSub?.unsubscribe();
    this.videoPlayer.stop();

    try {
      this.videoTexture?.destroy(true);
    } catch {}
    this.videoTexture = undefined;

    if (this.sprite) {
      try {
        this.container.removeChild(this.sprite);
        this.sprite.destroy();
      } catch {}
      this.sprite = undefined;
    }
  }
  maskRectangle: RectangleElement;

  _gsapAnimateStep(animation: StudioAnimation): gsap.core.Timeline {
    this.timeline = gsap.timeline();

    if (!animation) {
      return this.timeline;
    }

    const { from, to } = this.descriptor;
    // console.log('gsapAnimateStep', animation);
    // console.log('videoInstance', this.videoInstance);
    // console.log('video', this.video);
    // -- // -- //

    const duration = animation.duration ?? 1;

    // Resolve current target position once
    const targetX = this.container?.x ?? 0;
    const targetY = this.container?.y ?? 0;

    // Compute directional offset for float, based on current visual bounds
    const dx = 0;
    const dy = 0;

    const { width, height } = this.video;
    const offsetRatio = 0.25; // 25% of size
    const ox = (width || 320) * offsetRatio;
    const oy = (height || 180) * offsetRatio;

    const { mask, type } = animation;

    if (mask) {
      // -- // -- //
      this.maskRectangle = new RectangleElement(this, this.container, {
        width: width,
        height: height,
        fill: '#000000',
        opacity: 0.001,
        r: 24,
      });
      console.log('maskRectangle', this.maskRectangle);
      console.log('mask', mask, 'width', width, 'height', height);
      let maskTween: gsap.core.Tween;
      switch (mask) {
        case 'left':
          this.maskRectangle.element.x = -width;
          maskTween = gsap.to(this.maskRectangle.element, {
            x: 0,
            duration: 0.4,
            // -- // ease: 'power2.out', // -- //
          });
          break;
        case 'right':
          this.maskRectangle.element.x = width;
          maskTween = gsap.to(this.maskRectangle.element, {
            x: 0,
            duration: 0.4,
            // ease: 'power2.out',
          });
          break;
        case 'top':
          this.maskRectangle.element.y = -height;
          maskTween = gsap.to(this.maskRectangle.element, {
            y: 0,
            duration: 0.4,
            // ease: 'power2.out',
          });
          break;
        case 'bottom':
          this.maskRectangle.element.y = height;
          maskTween = gsap.to(this.maskRectangle.element, {
            y: 0,
            duration: 0.4,
            // ease: 'power2.out',
          });
          break;
      }
      if (maskTween) {
        maskTween.eventCallback('onComplete', () => {
          // -- //
          this.play();
        });

        this.timeline.add(maskTween, 0);
      }
      this.sprite.mask = this.maskRectangle.element;
    }

    // switch (animation.mask) {
    //   case 'left':
    //     dx = -ox;
    //     break;
    //   case 'right':
    //     dx = ox;
    //     break;
    //   case 'top':
    //     dy = -oy;
    //     break;
    //   case 'bottom':
    //     dy = oy;
    //     break;
    // }

    if (type === 'appear') {
      // Play video when animation starts
      const { from, to } = this.descriptor;
      console.log('from-to', from, to);
      this.timeline.to({}, { duration: to - from });
      this.timeline.call(() => this.play(), undefined, 0);
      // this.timeline.add(() => this.waitForVideoEnd());

      // Position tween from offset to target
      // this.timeline.fromTo(
      //   this.container,
      //   { x: targetX + dx, y: targetY + dy },
      //   { x: targetX, y: targetY, duration, ease: 'power2.out' },
      //   0,
      // );

      // // Opacity fade in
      // this.timeline.fromTo(
      //   this.container,
      //   { alpha: 0 },
      //   {
      //     alpha: 1,
      //     duration: Math.max(0.1, duration * 0.8),
      //     ease: 'power1.out',
      //   },
      //   0,
      // );

      // Optional scale tween
      // if (
      //   animation.scale &&
      //   (animation.scale.from !== undefined || animation.scale.to !== undefined)
      // ) {
      //   const baseX = this._scale?.x || 1;
      //   const baseY = this._scale?.y || 1;
      //   const from = animation.scale.from ?? 1;
      //   const to = animation.scale.to ?? 1;
      //   const scaleTarget = this.sprite?.scale || this.container.scale;
      //   this.timeline.fromTo(
      //     scaleTarget,
      //     { x: baseX * from, y: baseY * from },
      //     { x: baseX * to, y: baseY * to, duration, ease: 'power2.out' },
      //     0,
      //   );
      // }
    } else {
      // Disappear: move out along float direction and fade out
      this.timeline.to(
        this.container,
        { x: targetX + dx, y: targetY + dy, duration, ease: 'power2.in' },
        0,
      );
      this.timeline.to(
        this.container,
        {
          alpha: 0,
          duration: Math.max(0.1, duration * 0.7),
          ease: 'power1.in',
        },
        0,
      );

      // if (
      //   animation.scale &&
      //   (animation.scale.to !== undefined || animation.scale.from !== undefined)
      // ) {
      //   const baseX = this._scale?.x || 1;
      //   const baseY = this._scale?.y || 1;
      //   const to = animation.scale.to ?? 1;
      //   const scaleTarget = this.sprite?.scale || this.container.scale;
      //   tl.to(
      //     scaleTarget,
      //     { x: baseX * to, y: baseY * to, duration, ease: 'power2.in' },
      //     0,
      //   );
      // }
    }

    // Chain next animation step if provided
    // if (animation.next) {
    //   const nextAnim: VideoAnimationDescriptor = {
    //     baseType: animation.next.baseType,
    //     duration: animation.next.duration,
    //     float: animation.next.float,
    //     scale: animation.next.scale,
    //   };
    //   tl.add(this.gsapAnimateNew(nextAnim), `+=0`);
    // }
    this.timeline.eventCallback('onComplete', () => {
      this.stop();
    });

    super.gsapAnimateStep(animation);
    return this.timeline;
  }
}
